import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Text,
  Icon,
  Flex,
  Image,
  Divider,
  IconButton,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { SearchEntity } from "./globalSearch.entinty";
import { useGlobalSearch } from "../../store/hooks/useGlobalSearch";
import {
  ArrowUpLeftIcon,
  ShoppingBag,
  Send,
  ReceiptText,
  LayoutGrid,
  BedDouble,
  Car,
  Store,
} from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

type FilterType = SearchEntity | "all";

interface GlobalSearchProps {
  onResultSelect?: () => void;
  inlineResults?: boolean;
}

const SEARCH_OPTIONS: {
  label: string;
  value: FilterType;
  icon: React.ElementType;
  color: string;
}[] = [
  { label: "All", value: "all", icon: LayoutGrid, color: "#2563EB" },
  { label: "Products", value: "products", icon: ShoppingBag, color: "#14B8A6" },
  { label: "Hikes", value: "hikes", icon: Send, color: "#8B5CF6" },
  {
    label: "Accommodations",
    value: "accommodations",
    icon: BedDouble,
    color: "#F59E0B",
  },
  { label: "Car Hire", value: "carHire", icon: Car, color: "#F97316" },
  { label: "Orders", value: "orders", icon: ReceiptText, color: "#0EA5E9" },
  { label: "Businesses", value: "businesses", icon: Store, color: "#22C55E" },
];

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) {
    return `rgba(100,116,139,${alpha})`;
  }
  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const HStackLikeFix: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Flex align="center" gap={3} flex="1" minW={0}>
    {children}
  </Flex>
);

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  onResultSelect,
  inlineResults = false,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");
  const [showResults, setShowResults] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const results = useGlobalSearch(query, filter);
  const { theme } = useCustomTheme();

  const selectedOption =
    SEARCH_OPTIONS.find((o) => o.value === filter) ?? SEARCH_OPTIONS[0];

  const isDark = theme.name === "dark";

  const searchBg = isDark ? theme.surface || "#0F172A" : "#FFFFFF";
  const searchSoftBg = isDark
    ? theme.surfaceAlt || "rgba(255, 255, 255, 0.045)"
    : "#F8FAFC";
  const searchSelectedBg = isDark
    ? theme.selected || "rgba(20, 184, 166, 0.14)"
    : "#F1F5F9";
  const searchHoverBg = isDark
    ? theme.hover || "rgba(255, 255, 255, 0.065)"
    : "#F8FAFC";
  const searchText = isDark ? theme.text || "#F8FAFC" : "#0F172A";
  const searchMutedText = isDark ? theme.textMuted || "#CBD5E1" : "#475569";
  const searchSubtleText = isDark ? theme.textSubtle || "#94A3B8" : "#64748B";
  const searchBorder = isDark
    ? theme.border || "rgba(255, 255, 255, 0.08)"
    : "#E2E8F0";
  const searchBorderStrong = isDark
    ? theme.borderStrong || "rgba(255, 255, 255, 0.14)"
    : "#CBD5E1";
  const searchShadow = isDark
    ? "0 18px 48px rgba(0, 0, 0, 0.48)"
    : "0 16px 40px rgba(15, 23, 42, 0.14)";
  const searchInputShadow = isDark ? "0 10px 28px rgba(0, 0, 0, 0.32)" : "none";
  const accent = theme.brand?.electricBlue || theme.primary || "#14B8A6";
  const cardsBorderRadii = "8px";

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setShowOptions(false);
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleClearSearch = () => {
    setQuery("");
    setShowResults(false);
    setShowOptions(false);
  };

  const handleOptionSelect = (value: FilterType) => {
    setFilter(value);
    setShowOptions(false);

    if (query.trim()) {
      setShowResults(true);
    }
  };

  const renderContainedIcon = (
    IconComponent: React.ElementType,
    color: string,
    active = false,
    size = "18px",
    iconSize = "12px",
  ) => (
    <Flex
      w={size}
      h={size}
      borderRadius={cardsBorderRadii}
      align="center"
      justify="center"
      bg={hexToRgba(color, active ? 0.24 : 0.16)}
      border={`1px solid ${hexToRgba(color, active ? 0.38 : 0.24)}`}
      boxShadow={
        active
          ? isDark
            ? "0 2px 10px rgba(0, 0, 0, 0.26)"
            : "0 2px 8px rgba(15, 23, 42, 0.08)"
          : "none"
      }
      transition="all 0.2s ease"
      flexShrink={0}
    >
      <Icon
        as={IconComponent}
        boxSize={iconSize}
        color={color}
        fill={hexToRgba(color, active ? 0.18 : 0.1)}
        strokeWidth={2.2}
      />
    </Flex>
  );

  const resolveRoute = (item: any) => {
    if (item?.route) return item.route;

    const id = item?.id ?? item?._id;
    const type = String(item?.type || "").toLowerCase();

    if (!id) return "/";
    if (type === "orders") return `/search-details/orders/${id}`;
    if (type === "hikes") return `/search-details/hikes/${id}`;
    if (type === "accommodations")
      return `/search-details/accommodations/${id}`;
    if (type === "carhire") return `/search-details/cars/${id}`;
    if (type === "products") return `/product/${id}`;
    if (type === "businesses") return `/search-details/businesses/${id}`;

    return `/search-details/${type}/${id}`;
  };

  const resolveState = (item: any) => {
    if (item?.state) return item.state;

    const type = String(item?.type || "").toLowerCase();
    if (type === "orders") return { order: item };
    if (type === "hikes") return { hike: item };
    if (type === "accommodations") return { accommodation: item };
    if (type === "carhire") return { car: item };
    if (type === "businesses") return { business: item };

    return undefined;
  };

  return (
    <Box
      ref={containerRef}
      w="100%"
      position="relative"
      maxW="100%"
      bg={searchBg}
      borderRadius={cardsBorderRadii}
      color={searchText}
    >
      <Flex
        border={`1px solid ${searchBorderStrong}`}
        borderRadius={cardsBorderRadii}
        bg={searchBg}
        overflow="hidden"
        color={searchText}
        boxShadow={searchInputShadow}
        _hover={{ borderColor: accent }}
        _focusWithin={{
          borderColor: accent,
          boxShadow: `0 0 0 1px ${hexToRgba(accent, isDark ? 0.48 : 0.34)}`,
        }}
        transition="all 0.2s ease"
        w="100%"
      >
        <Flex
          as="button"
          type="button"
          px={{
            base: selectedOption.value === "all" ? 3 : 3.5,
            md: selectedOption.value === "all" ? 3 : 4,
          }}
          bg={searchSoftBg}
          color={searchText}
          borderRight={`1px solid ${searchBorder}`}
          _hover={{ bg: searchHoverBg, color: searchText }}
          _active={{ bg: searchSelectedBg, color: searchText }}
          _focus={{ boxShadow: "none", color: searchText }}
          align="center"
          justify="space-between"
          gap={2}
          cursor="pointer"
          onClick={() => {
            setShowOptions((prev) => !prev);
            setShowResults(false);
          }}
          flex="0 0 auto"
          width="auto"
          minW="fit-content"
          maxW={{ base: "48%", md: "220px" }}
          whiteSpace="nowrap"
        >
          <Text
            fontWeight="600"
            fontSize="sm"
            noOfLines={1}
            maxW={{ base: "120px", md: "170px" }}
          >
            {selectedOption.label}
          </Text>
          <ChevronDownIcon flexShrink={0} />
        </Flex>

        <InputGroup bg={searchBg} flex="1" minW={0}>
          <InputLeftElement
            pointerEvents="none"
            borderLeft={`1px solid ${searchBorder}`}
          >
            <Icon as={SearchIcon} color={searchSubtleText} />
          </InputLeftElement>

          <Input
            placeholder="Search products, hikes, accommodations, car hire, businesses..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowOptions(false);
              setShowResults(!!e.target.value.trim());
            }}
            onFocus={() => {
              setShowOptions(false);
              if (query.trim()) setShowResults(true);
            }}
            border="none"
            color={searchText}
            _placeholder={{ color: searchSubtleText }}
            _hover={{ border: "none" }}
            _focus={{ border: "none", boxShadow: "none" }}
            bg={searchBg}
            pr={query ? "48px" : undefined}
          />

          {query && (
            <InputRightElement>
              <IconButton
                aria-label="Clear search"
                icon={<CloseIcon boxSize={2.5} />}
                size="sm"
                variant="ghost"
                color={searchMutedText}
                bg="transparent"
                _hover={{ bg: searchHoverBg, color: searchText }}
                _active={{ bg: searchSelectedBg }}
                onClick={handleClearSearch}
              />
            </InputRightElement>
          )}
        </InputGroup>
      </Flex>

      {showOptions && (
        <Box
          position={inlineResults ? "relative" : "absolute"}
          top={inlineResults ? undefined : "100%"}
          left={inlineResults ? undefined : 0}
          right={inlineResults ? undefined : 0}
          w="100%"
          mt={2}
          bg={searchBg}
          color={searchText}
          borderRadius={cardsBorderRadii}
          border={`1px solid ${searchBorder}`}
          boxShadow={searchShadow}
          zIndex={4000}
          p={2}
          maxH={{
            base: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 140px)",
            md: "420px",
          }}
          overflowY="auto"
          sx={{
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          }}
        >
          {SEARCH_OPTIONS.map((option) => {
            const isActive = filter === option.value;

            return (
              <Box
                key={option.value}
                as="button"
                type="button"
                width="100%"
                display="block"
                textAlign="left"
                color={searchMutedText}
                onClick={() => handleOptionSelect(option.value)}
                borderRadius={cardsBorderRadii}
                bg={isActive ? searchSelectedBg : searchBg}
                border={`1px solid ${
                  isActive ? hexToRgba(option.color, 0.28) : searchBorder
                }`}
                mb={1.5}
                _last={{ mb: 0 }}
                _hover={{ bg: searchHoverBg, color: searchText }}
                _focus={{ bg: searchHoverBg, color: searchText }}
                transition="all 0.2s ease"
              >
                <Flex
                  align="center"
                  w="100%"
                  justify="space-between"
                  gap={3}
                  px={3}
                  py={3}
                >
                  <HStackLikeFix>
                    {renderContainedIcon(
                      option.icon,
                      option.color,
                      isActive,
                      "34px",
                      "18px",
                    )}
                    <Box
                      flex="1"
                      fontWeight="600"
                      color={isActive ? searchText : searchMutedText}
                    >
                      {option.label}
                    </Box>
                  </HStackLikeFix>

                  <Icon
                    as={ArrowUpLeftIcon}
                    color={isActive ? option.color : searchSubtleText}
                    boxSize={4}
                  />
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}

      {query && showResults && (
        <Box
          position={inlineResults ? "relative" : "absolute"}
          top={inlineResults ? undefined : "100%"}
          left={inlineResults ? undefined : 0}
          right={inlineResults ? undefined : 0}
          w="100%"
          mt={2}
          bg={searchBg}
          color={searchText}
          borderRadius={cardsBorderRadii}
          border={`1px solid ${searchBorder}`}
          boxShadow={searchShadow}
          zIndex={3500}
          p={2}
          maxH={{
            base: inlineResults
              ? "none"
              : "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 160px)",
            md: "420px",
          }}
          overflowY={inlineResults ? "visible" : "auto"}
          sx={{
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          }}
        >
          {results.length === 0 ? (
            <Text
              p={4}
              textAlign="center"
              fontStyle="italic"
              color={searchSubtleText}
              bg={searchSoftBg}
              border="1px solid"
              borderColor={searchBorder}
              borderRadius={cardsBorderRadii}
            >
              No results found for “{query}”
            </Text>
          ) : (
            <>
              <Divider borderColor={searchBorder} mb={2} />
              <List spacing={1.5}>
                {results.map((item) => (
                  <ListItem
                    key={`${item.type}-${item.id}`}
                    px={3}
                    py={3}
                    cursor="pointer"
                    _hover={{ bg: searchHoverBg }}
                    border="1px solid"
                    borderColor={searchBorder}
                    borderRadius={cardsBorderRadii}
                    bg={searchBg}
                    onClick={() => {
                      navigate(resolveRoute(item), {
                        state: resolveState(item),
                      });
                      setQuery("");
                      setShowResults(false);
                      setShowOptions(false);
                      onResultSelect?.();
                    }}
                  >
                    <Flex align="center" gap={3}>
                      {item.image && (
                        <Image
                          src={item.image}
                          boxSize="32px"
                          borderRadius={cardsBorderRadii}
                          objectFit="cover"
                          border={`1px solid ${searchBorder}`}
                          bg={searchSoftBg}
                        />
                      )}

                      <Box flex={1} minW={0}>
                        <Text
                          fontWeight="600"
                          color={searchText}
                          fontSize="sm"
                          noOfLines={1}
                        >
                          {item.title}
                        </Text>
                        {item.description && (
                          <Text
                            fontSize="xs"
                            color={searchMutedText}
                            noOfLines={1}
                          >
                            {item.description}
                          </Text>
                        )}
                      </Box>

                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        textTransform="uppercase"
                        color={searchSubtleText}
                        px={2}
                        py={1}
                        borderRadius={cardsBorderRadii}
                        bg={searchSoftBg}
                        border={`1px solid ${searchBorder}`}
                        whiteSpace="nowrap"
                      >
                        {item.type}
                      </Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GlobalSearch;
